const STYLE_ID = 'cardea-dialog-styles'

const ensureStyles = () => {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) {
    return
  }

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    .cardea-dialog {
      width: min(860px, 92vw);
      max-height: 86vh;
      border: 0;
      border-radius: 4px;
      padding: 0;
      box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
      color: #2b2b2b;
    }
    .cardea-dialog--sm {
      width: min(420px, 92vw);
    }
    .cardea-dialog--lg {
      width: min(980px, 96vw);
    }
    .cardea-dialog::backdrop {
      background: rgba(0, 0, 0, 0.45);
    }
    .cardea-dialog__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px 12px;
      border-bottom: 1px solid #e6e6e6;
    }
    .cardea-dialog__title {
      margin: 0;
      font-size: 28px;
      line-height: 1;
      color: #379897;
      font-family: "Oswald", "Helvetica Neue", Helvetica, Arial, sans-serif;
      text-transform: none;
    }
    .cardea-dialog__close {
      border: 0;
      background: transparent;
      color: #9bc7c7;
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      padding: 2px 6px;
    }
    .cardea-dialog__body {
      padding: 18px 20px;
      overflow: auto;
      max-height: calc(86vh - 136px);
      white-space: pre-wrap;
    }
    .cardea-dialog__footer {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      padding: 16px 20px;
      border-top: 1px solid #e6e6e6;
    }
    .cardea-dialog__input {
      width: 100%;
      margin-top: 10px;
      padding: 8px 10px;
      border: 1px solid #dce4ec;
      border-radius: 2px;
      font-size: 12px;
    }
  `
  document.head.appendChild(style)
}

const normalizePayload = (payload = {}) => {
  if (typeof payload === 'string') {
    return { content: payload }
  }

  return payload || {}
}

const setBodyContent = (body, payload) => {
  const content = payload.content || payload.message || ''
  if (payload.html) {
    body.innerHTML = content
  } else {
    body.textContent = content
  }
}

const button = ({ text, className, value, type = 'submit' }) => {
  const el = document.createElement('button')
  el.type = type
  el.className = className
  if (value) {
    el.value = value
  }
  el.textContent = text
  return el
}

const removeDialog = (dialog) => {
  if (dialog && dialog.parentNode) {
    dialog.parentNode.removeChild(dialog)
  }
}

const createDialog = (kind, payload, resolve) => {
  ensureStyles()

  const dialog = document.createElement('dialog')
  const size = payload.size || (kind === 'alert' ? 'sm' : 'lg')
  dialog.className = `cardea-dialog cardea-dialog--${size}`

  const form = document.createElement('form')
  form.method = 'dialog'

  const titleText = payload.title || ''
  const showClose = payload.showClose === true || kind !== 'alert'
  const showHeader = Boolean(titleText) || showClose

  let header = null
  if (showHeader) {
    header = document.createElement('header')
    header.className = 'cardea-dialog__header'

    const title = document.createElement('h3')
    title.className = 'cardea-dialog__title'
    title.textContent = titleText
    header.appendChild(title)

    if (showClose) {
      const close = document.createElement('button')
      close.type = 'button'
      close.className = 'cardea-dialog__close'
      close.setAttribute('aria-label', 'Fechar')
      close.innerHTML = '&times;'
      close.addEventListener('click', () => dialog.close('cancel'))
      header.appendChild(close)
    }
  }

  const body = document.createElement('div')
  body.className = 'cardea-dialog__body'
  setBodyContent(body, payload)

  let promptInput = null
  if (kind === 'prompt') {
    promptInput = document.createElement('input')
    promptInput.className = 'cardea-dialog__input'
    promptInput.type = 'text'
    promptInput.value = payload.value || ''
    body.appendChild(promptInput)
  }

  const footer = document.createElement('footer')
  footer.className = 'cardea-dialog__footer'

  if (kind !== 'alert') {
    const cancel = button({
      text: 'Cancelar',
      className: 'btn btn-default',
      type: 'button',
    })
    cancel.addEventListener('click', () => dialog.close('cancel'))
    footer.appendChild(cancel)
  }

  const ok = button({
    text: 'OK',
    className: 'btn btn-primary',
    value: 'ok',
  })
  footer.appendChild(ok)

  if (header) {
    form.appendChild(header)
  }
  form.appendChild(body)
  form.appendChild(footer)
  dialog.appendChild(form)
  document.body.appendChild(dialog)

  dialog.addEventListener('close', () => {
    const rv = dialog.returnValue
    if (kind === 'alert') {
      resolve(true)
    } else if (kind === 'confirm') {
      if (rv === 'ok') {
        resolve(true)
      }
    } else if (kind === 'prompt') {
      if (rv === 'ok') {
        resolve(promptInput ? promptInput.value : '')
      }
    }
    removeDialog(dialog)
  })

  if (typeof dialog.showModal === 'function') {
    dialog.showModal()
    if (promptInput) {
      setTimeout(() => promptInput.focus(), 0)
    }
    return
  }

  // Fallback for very old browsers
  if (kind === 'alert') {
    window.alert(payload.content || payload.message || '')
    resolve(true)
  } else if (kind === 'confirm') {
    if (window.confirm(payload.content || payload.message || '')) {
      resolve(true)
    }
  } else if (kind === 'prompt') {
    const value = window.prompt(payload.content || payload.message || '', '')
    if (value !== null) {
      resolve(value)
    }
  }
  removeDialog(dialog)
}

const createDialogAPI = () => ({
  alert(payload) {
    const normalized = normalizePayload(payload)
    return new Promise((resolve) => createDialog('alert', normalized, resolve))
  },
  confirm(payload) {
    const normalized = normalizePayload(payload)
    return new Promise((resolve) =>
      createDialog('confirm', normalized, resolve)
    )
  },
  prompt(payload) {
    const normalized = normalizePayload(payload)
    return new Promise((resolve) => createDialog('prompt', normalized, resolve))
  },
})

const DialogGlobalsPlugin = {
  install(app) {
    const api = createDialogAPI()
    app.config.globalProperties.$alert = api.alert
    app.config.globalProperties.$confirm = api.confirm
    app.config.globalProperties.$prompt = api.prompt

    if (typeof window !== 'undefined') {
      window.__cardeaDialog = api
    }
  },
}

export default DialogGlobalsPlugin
